import React from 'react';
import classNames from 'classnames/bind';
import { Skeleton } from '@mantine/core';
import { UseQueryResult } from '@tanstack/react-query';

import APIError from 'types/api_error';

import { FilesSearchResult } from 'types/files_search_result';
import { LumeoFileWithSearchMeta } from 'types/file_with_search_meta';
import { CursorPaginated, OffsetPaginated } from 'types/pagination';

import { isNotNullOrUndefined } from 'services/nullable';
import { useCameras } from 'cameras/hooks/useCameras';
import { useCheckFilesExistence } from 'files/hooks/useCheckFilesExistence';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useStreams } from 'streams/hooks/useStreams';

import { SkeletonList } from 'components/Skeleton/SkeletonList';
import { DateRange } from 'components/DateTimeRangeInput/DateTimeRangeInput';
import { Thumbnail } from 'components/Thumbnail/Thumbnail';
import { useTrackableNames } from 'trackables/hooks';

import { FilesSearchItem } from './Item';
import styles from './Search.module.scss';

const c = classNames.bind(styles);

type FilesSearchResultsProps = {
  dateRange: DateRange;
  queryResult: UseQueryResult<
    | OffsetPaginated<FilesSearchResult | LumeoFileWithSearchMeta>
    | CursorPaginated<LumeoFileWithSearchMeta>,
    APIError
  >;
};

export function FilesSearchResults({
  dateRange,
  queryResult,
}: FilesSearchResultsProps) {
  const { isLoading: isLoadingPlan } = useCurrentPlan();
  const {
    isLoading: isLoadingFiles,
    isPreviousData,
    isFetching,
    data,
  } = queryResult;

  const streamIds: string[] = [
    ...new Set(
      data?.data.map(({ stream_id }) => stream_id).filter(isNotNullOrUndefined)
    ),
  ];

  const { data: streams, isLoading: isLoadingStreams } = useStreams({
    params: {
      stream_ids: streamIds,
      limit: streamIds.length,
    },
    enabled: streamIds.length > 0,
    keepPreviousData: true,
  });

  const fileIds = data?.data.map((file) =>
    'file_id' in file ? file.file_id : file.id
  );
  const { isLoading: isLoadingFilesExistence, data: filesExistence } =
    useCheckFilesExistence({ params: { file_ids: fileIds } });

  const streamsCamerasMap: Record<string, string> =
    streams?.data.reduce(
      (acc, { id, camera_id }) => {
        if (camera_id) {
          acc[id] = camera_id;
        }
        return acc;
      },
      {} as Record<string, string>
    ) ?? {};
  const cameraIds = [...new Set(Object.values(streamsCamerasMap))];

  const { data: cameras, isLoading: isLoadingCameras } = useCameras({
    params: {
      camera_ids: cameraIds,
      limit: cameraIds.length,
    },
    enabled: cameraIds.length > 0,
  });

  const trackableIds = React.useMemo(() => {
    const ids = new Set<string>();

    (data?.data ?? []).forEach((item) => {
      const trackables =
        'trackables' in item && Array.isArray(item.trackables)
          ? item.trackables
          : [];
      for (const id of trackables) {
        if (id) {
          ids.add(id);
        }
      }
    });

    return Array.from(ids);
  }, [data]);

  const trackableNamesQuery = useTrackableNames(trackableIds);
  const trackableItemsLookup = trackableNamesQuery.data?.items;
  const trackableListsLookup = trackableNamesQuery.data?.lists;

  const isLoading =
    isLoadingFiles ||
    isLoadingStreams ||
    isLoadingCameras ||
    isLoadingPlan ||
    isLoadingFilesExistence;

  if ((isLoading && !data) || (isFetching && isPreviousData)) {
    return (
      <div className={c('grid')}>
        <SkeletonList
          min={data && data.data.length > 0 ? data.data.length : 48}
          max={data && data.data.length > 0 ? data.data.length : 48}
          component={
            <div className={c('item-view-button')}>
              <Thumbnail isLoading />
              <Skeleton height="1rem" width="30%" visible />
              <Skeleton height="1.5rem" width="90%" visible />
            </div>
          }
        />
      </div>
    );
  }

  if (!data || data.data.length === 0) {
    return <div className={c('info')}>No results</div>;
  }

  return (
    <div className={c('grid')}>
      {data.data.map((item) => {
        const cameraId = item.stream_id && streamsCamerasMap[item.stream_id];
        const isFromCameraStream = Boolean(item.stream_id && cameraId);

        const source = isFromCameraStream
          ? cameras?.data.find(({ id }) => id === cameraId)
          : streams?.data.find(({ id }) => id === item.stream_id);

        // FilesSearchResult
        if ('file_name' in item) {
          const isDeleted = Boolean(
            filesExistence && !filesExistence[item.file_id]
          );

          return (
            <FilesSearchItem
              key={item.file_id}
              id={item.file_id}
              initialDateRange={dateRange}
              name={item.file_name}
              thumbnail_url={item.thumbnail_url}
              mime_type={item.mime_type}
              created_at={item.created_at}
              events={item.events}
              objects={item.objects}
              texts={item.texts}
              trackables={Array.isArray(item.trackables) ? item.trackables : []}
              search_score={item.search_score}
              stream_id={item.stream_id}
              source={source}
              size={item.file_size}
              isDeleted={isDeleted}
              trackableItemsLookup={trackableItemsLookup}
              trackableListsLookup={trackableListsLookup}
            />
          );
        }

        // LumeoFileWithSearchMeta
        const isDeleted = Boolean(filesExistence && !filesExistence[item.id]);

        return (
          <FilesSearchItem
            key={item.id}
            id={item.id}
            initialDateRange={dateRange}
            name={item.name}
            thumbnail_url={item.thumbnail_url}
            mime_type={item.mime_type}
            created_at={item.created_at}
            events={item.events ?? []}
            objects={item.objects ?? []}
            texts={item.texts ?? []}
            stream_id={item.stream_id}
            source={source}
            size={item.size}
            isDeleted={isDeleted}
            trackables={Array.isArray(item.trackables) ? item.trackables : []}
            search_score={item.search_score}
            trackableItemsLookup={trackableItemsLookup}
            trackableListsLookup={trackableListsLookup}
          />
        );
      })}
    </div>
  );
}
